import React, { useState } from 'react';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTools } from '@/icons';
import { apiStatusManager } from '../services/ApiStatusManager';

/**
 * Trouble page displayed when the API is down
 */
export const TroublePage: React.FC = () => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const [isRetrying, setIsRetrying] = useState(false);

    const containerStyle: React.CSSProperties = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        textAlign: 'center',
        backgroundColor: theme.colors.background,
        color: theme.colors.text.primary,
    };

    const iconStyle = {
        fontSize: '4rem',
        marginBottom: '2rem',
        color: theme.colors.primary,
    } as React.CSSProperties;

    const titleStyle: React.CSSProperties = {
        ...themedStyles.text.heading,
        fontSize: '2rem',
        marginBottom: '1rem',
    };

    const messageStyle: React.CSSProperties = {
        ...themedStyles.text.body,
        fontSize: '1.2rem',
        marginBottom: '1rem',
        opacity: 0.8,
        maxWidth: '600px',
    };

    const linkStyle: React.CSSProperties = {
        color: theme.colors.primary,
        textDecoration: 'none',
        fontWeight: theme.typography.fontWeight.medium,
    };

    const handleTryAgain = async () => {
        setIsRetrying(true);
        try {
            await apiStatusManager.retryLastRequest();
            // If successful, the API status will be updated and the page will disappear
            // If it fails, we'll catch the error and stay on the trouble page
        } catch (error) {
            // Request still failing, stay on trouble page
            console.warn('Retry failed:', error);
        } finally {
            setIsRetrying(false);
        }
    };

    const buttonStyle: React.CSSProperties = {
        ...themedStyles.button.primary,
        ...(isRetrying ? themedStyles.button.disabled : {}),
        marginTop: theme.spacing.md,
    };

    return (
        <div style={containerStyle}>
            <FontAwesomeIcon icon={faTools} style={iconStyle as any} />
            <h1 style={titleStyle}>Trouble reaching the server</h1>
            <p style={messageStyle}>
                We're having trouble connecting to the server. Please try again, or contact support if the problem persists.
            </p>
            <p style={messageStyle}>
                <a href="mailto:support@calculist.app" style={linkStyle}>
                    support@calculist.app
                </a>
            </p>
            <button
                onClick={handleTryAgain}
                disabled={isRetrying}
                style={buttonStyle}
            >
                {isRetrying ? 'Trying Again...' : 'Try Again'}
            </button>
        </div>
    );
};
